class VideoPlayer {
  constructor() {
    this.videoPlayers = [];
    this.init();
  }

  init() {
    document.querySelectorAll('[data-video-container]').forEach(container => {
      const video = container.querySelector('[data-video-player]');
      const playButton = container.querySelector('[data-video-play]');
      const poster = container.querySelector('[data-video-poster]');
      const controls = container.querySelector('[data-video-controls]');

      if (video && playButton) {
        this.videoPlayers.push({
          container,
          video,
          playButton,
          poster,
          controls
        });

        this.setupEventListeners(container, video, playButton, poster, controls);
      }
    });
  }

  setupEventListeners(container, video, playButton, poster, controls) {
    playButton.addEventListener('click', () => {
      this.togglePlay(video, playButton, poster);
    });

    video.addEventListener('click', () => {
      this.togglePlay(video, playButton, poster);
    });

    if (controls) {
      const playPauseBtn = controls.querySelector('[data-play-pause]');
      const muteBtn = controls.querySelector('[data-mute]');
      const fullscreenBtn = controls.querySelector('[data-fullscreen]');
      const seek = controls.querySelector('[data-seek]');
      const progressBar = controls.querySelector('[data-progress-bar]');
      const currentTimeEl = controls.querySelector('[data-current-time]');
      const durationEl = controls.querySelector('[data-duration]');

      playPauseBtn.addEventListener('click', () => {
        this.togglePlay(video, playButton, poster);
      });

      muteBtn.addEventListener('click', () => {
        video.muted = !video.muted;
        this.updateMuteButton(muteBtn, video.muted);
      });
      fullscreenBtn.addEventListener('click', () => {
        this.toggleFullscreen(container);
      });

      seek.addEventListener('input', () => {
        const time = (seek.value / 100) * video.duration;
        video.currentTime = time;
      });

      video.addEventListener('timeupdate', () => {
        if (!video.duration) return;
        
        const progress = (video.currentTime / video.duration) * 100;
        seek.value = progress;
        progressBar.style.width = `${progress}%`;
        currentTimeEl.textContent = this.formatTime(video.currentTime);
      });

      video.addEventListener('loadedmetadata', () => {
        if (video.duration) {
          durationEl.textContent = this.formatTime(video.duration);
        }
      });

      video.addEventListener('play', () => {
        this.updatePlayButton(playPauseBtn, true);
        this.hidePlayButton(playButton, poster);
      });

      video.addEventListener('pause', () => {
        this.updatePlayButton(playPauseBtn, false);
      });

      video.addEventListener('ended', () => {
        this.showPlayButton(playButton, poster);
        this.updatePlayButton(playPauseBtn, false);
      });

      let controlsTimeout;
      container.addEventListener('mousemove', () => {
        if (controls) {
          controls.style.opacity = '1';
          clearTimeout(controlsTimeout);
          controlsTimeout = setTimeout(() => {
            if (!video.paused) {
              controls.style.opacity = '0';
            }
          }, 3000);
        }
      });

      container.addEventListener('mouseleave', () => {
        if (controls && !video.paused) {
          controls.style.opacity = '0';
        }
      });
    }

    video.addEventListener('ended', () => {
      this.showPlayButton(playButton, poster);
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && !video.paused) {
            video.pause();
            this.showPlayButton(playButton, poster);
          }
        });
      }, { threshold: 0.5 });

      observer.observe(container);
    }
  }

  togglePlay(video, playButton, poster) {
    if (video.paused || video.ended) {
      video.play();
      this.hidePlayButton(playButton, poster);
    } else {
      video.pause();
      this.showPlayButton(playButton, poster);
    }
  }

  hidePlayButton(playButton, poster) {
    if (playButton) playButton.style.display = 'none';
    if (poster) poster.style.display = 'none';
  }

  showPlayButton(playButton, poster) {
    if (playButton) playButton.style.display = 'flex';
    if (poster) poster.style.display = 'block';
  }

  updatePlayButton(button, playing) {
    if (!button) return;
    
    const playIcon = button.querySelector('.play-icon');
    const pauseIcon = button.querySelector('.pause-icon');
    
    if (playing) {
      playIcon.style.display = 'none';
      pauseIcon.style.display = 'block';
    } else {
      playIcon.style.display = 'block';
      pauseIcon.style.display = 'none';
    }
  }

  updateMuteButton(button, muted) {
    if (!button) return;
    
    const volumeOn = button.querySelector('.volume-on');
    const volumeOff = button.querySelector('.volume-off');
    
    if (muted) {
      volumeOn.style.display = 'none';
      volumeOff.style.display = 'block';
    } else {
      volumeOn.style.display = 'block';
      volumeOff.style.display = 'none';
    }
  }

  formatTime(seconds) {
    if (isNaN(seconds)) return '0:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  toggleFullscreen(container) {
    if (!document.fullscreenElement) {
      container.requestFullscreen?.().catch(err => {
        console.log(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen?.();
    }
  }

  pauseAllVideos() {
    this.videoPlayers.forEach(({ video, playButton, poster }) => {
      video.pause();
      this.showPlayButton(playButton, poster);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.videoPlayer = new VideoPlayer();
});

if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', () => {
    if (window.videoPlayer) {
      window.videoPlayer.init();
    }
  });
}

class VideoPopup {
  constructor(videoUrl) {
    this.videoUrl = videoUrl;
    this.createPopup();
  }

  createPopup() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'video-popup-overlay';
    this.overlay.innerHTML = `
      <div class="video-popup-container">
        <button class="video-popup-close" aria-label="Close">×</button>
        <video controls autoplay class="video-popup-player">
          <source src="${this.videoUrl}" type="video/mp4">
        </video>
      </div>
    `;

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    this.overlay.querySelector('.video-popup-close').addEventListener('click', () => {
      this.close();
    });

    document.body.appendChild(this.overlay);
    document.body.style.overflow = 'hidden';
  }

  close() {
    const video = this.overlay.querySelector('video');
    if (video) {
      video.pause();
    }
    this.overlay.remove();
    document.body.style.overflow = '';
  }
}

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.video-square-container').forEach(container => {
    const video = container.querySelector('[data-video-preview]');
    const playBtn = container.querySelector('.video-square-play-btn');
    const videoSrc = video.querySelector('source').src;

    container.addEventListener('click', (e) => {
      if (e.target.closest('.video-popup-close')) return;
      new VideoPopup(videoSrc);
    });

    video.addEventListener('click', (e) => {
      e.preventDefault();
    });

    if (playBtn) {
      playBtn.addEventListener('click', (e) => {
        e.stopPropagation(); 
        new VideoPopup(videoSrc);
      });
    }
  });
});